import React, { useEffect, useState } from 'react'
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Input,
  useColorModeValue,
  Badge,
  IconButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
  Button,
  InputGroup,
  InputLeftElement,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  VStack
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faSync,
  faInfoCircle,
  faHistory,
  faEye,
  faCheckCircle,
  faFileAlt,
  faRocket,
  faCopy
} from '@fortawesome/free-solid-svg-icons'
import { apiGetRequest } from '@/functions/requests'
import { CourseLog, AppMeta } from '@xnav/types/src/common'
import { motion, AnimatePresence } from 'framer-motion'
import Loading from '@/components/loading'

// Helper component for log details
const DetailsCell: React.FC<{ details: string }> = ({ details }) => {
  const textColor = useColorModeValue('gray.700', 'gray.200');

  if (!details) return <Text color="gray.500" fontSize="xs" fontStyle="italic">-</Text>;

  return (
    <Box minW="260px" maxW="600px" py={0.5}>
      <Text
        fontSize="xs"
        color={textColor}
        lineHeight="1.5"
        wordBreak="break-word"
        whiteSpace="pre-wrap"
        fontWeight="medium"
      >
        {details}
      </Text>
    </Box>
  );
};

// Helper for dynamic action badge coloring
const getActionColorScheme = (action: string) => {
  const act = action.toUpperCase()
  if (act.includes('DELETE') || act.includes('REMOVE') || act.includes('DISAPPROVED') || act.includes('FAIL')) {
    return 'red'
  }
  if (act.includes('CREATE') || act.includes('DEPLOY') || act.includes('APPROVED') || act.includes('SUCCESS')) {
    return 'green'
  }
  if (act.includes('CONTENT') || act.includes('MARKUP') || act.includes('QUIZ') || act.includes('FLASHCARD') || act.includes('SAVE')) {
    return 'teal'
  }
  return 'gray'
}

export default function GlobalLogs() {
  const [logs, setLogs] = useState<CourseLog[]>([])
  const [apps, setApps] = useState<Record<string, { name: string; creator: string }>>({})
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const toast = useToast()

  const cardBg = useColorModeValue('white', '#111827')
  const cardSubtleBg = useColorModeValue('gray.50', '#1f2937')
  const borderColor = useColorModeValue('gray.200', '#374151')
  const tableHeaderBg = useColorModeValue('gray.50', '#0f172a')
  const titleColor = useColorModeValue('gray.900', 'white')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')
  const hoverBg = useColorModeValue('gray.50', '#1e293b')

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const appsData = await apiGetRequest<undefined, AppMeta[]>('/apps')
      const appsMap: Record<string, { name: string; creator: string }> = {}
      if (appsData && Array.isArray(appsData)) {
        appsData.forEach(app => {
          appsMap[app.id] = { name: app.name, creator: app.creator || 'Unknown' }
        })
      }
      setApps(appsMap)

      const data = await apiGetRequest<undefined, CourseLog[]>('/logs')
      if (data && Array.isArray(data)) {
        setLogs(data.sort((a, b) => b.timestamp - a.timestamp))
      }
    } catch (error) {
      console.error('Failed to fetch logs', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLogs()
  }, [])

  const filteredLogs = logs.filter(
    log =>
      log.userEmail.toLowerCase().includes(search.toLowerCase()) ||
      log.action.toLowerCase().includes(search.toLowerCase()) ||
      log.appId.toLowerCase().includes(search.toLowerCase()) ||
      (apps[log.appId] && apps[log.appId].name.toLowerCase().includes(search.toLowerCase()))
  )

  const DEPLOYMENT_ACTIONS = [
    'DEPLOY REQUESTED',
    'DELETE REQUESTED',
    'DEPLOY DISAPPROVED',
    'DELETE DISAPPROVED',
    'DEPLOY REQUEST CANCELLED',
    'DELETE REQUEST CANCELLED',
    'DEPLOY DISAPPROVAL ACKNOWLEDGED',
    'DELETE DISAPPROVAL ACKNOWLEDGED',
    'COURSE DEPLOYED',
    'DEPLOYMENT DELETED'
  ]

  const isDeploymentLog = (action: string) => {
    return DEPLOYMENT_ACTIONS.includes(action.toUpperCase())
  }

  const courseLogs = filteredLogs.filter(log => !isDeploymentLog(log.action))
  const deploymentLogs = filteredLogs.filter(log => isDeploymentLog(log.action))

  // Group logs by appId for modification logs tab
  const courseLogsByApp: Record<string, CourseLog[]> = {}
  courseLogs.forEach(log => {
    if (!courseLogsByApp[log.appId]) {
      courseLogsByApp[log.appId] = []
    }
    courseLogsByApp[log.appId].push(log)
  })

  if (loading) {
    return <Loading message="Loading System Audit Logs..." />
  }

  return (
    <Flex flexDir="column" gap={6} w="100%" h="100%" maxW="1500px" mx="auto" p={{ base: 4, md: 6 }} overflowY="auto">
      {/* Header Bar */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color={titleColor}>
            Audit & System Logs
          </Heading>
          <Text color={mutedTextColor} fontSize="sm" mt={0.5}>
            Immutable global audit trail of all course modifications, authoring activities, and deployments.
          </Text>
        </Box>
        <HStack spacing={2.5}>
          <Button
            size="sm"
            variant="outline"
            borderColor={borderColor}
            leftIcon={<FontAwesomeIcon icon={faSync} />}
            onClick={fetchLogs}
            borderRadius="lg"
            fontSize="xs"
          >
            Refresh Feed
          </Button>
          <Badge bg="rgba(14, 165, 233, 0.1)" color="#0284c7" px={3} py={1.5} borderRadius="lg" fontSize="xs" fontWeight="700">
            {logs.length} Total Events
          </Badge>
        </HStack>
      </Flex>

      {/* Main Container */}
      <Box bg={cardBg} p={{ base: 4, md: 6 }} borderRadius="xl" border="1px solid" borderColor={borderColor}>
        {/* Search Input */}
        <Flex mb={5} gap={4}>
          <InputGroup size="sm" maxW="450px">
            <InputLeftElement pointerEvents="none">
              <FontAwesomeIcon icon={faSearch} color="#9ca3af" />
            </InputLeftElement>
            <Input
              placeholder="Search by course name, user email, course ID, or action..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              fontSize="xs"
              bg={cardSubtleBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
              _focus={{
                borderColor: '#0ea5e9',
                boxShadow: '0 0 0 1px #0ea5e9',
                bg: cardBg
              }}
            />
          </InputGroup>
        </Flex>

        <Tabs variant="soft-rounded">
          <TabList mb={5} bg={cardSubtleBg} p={1} borderRadius="xl" display="inline-flex" border="1px solid" borderColor={borderColor}>
            <Tab fontSize="xs" fontWeight="600" borderRadius="lg" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>
              <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '6px' }} />
              Course Modifications ({courseLogs.length})
            </Tab>
            <Tab fontSize="xs" fontWeight="600" borderRadius="lg" _selected={{ bgGradient: 'linear(to-r, #0ea5e9, #10b981)', color: 'white' }}>
              <FontAwesomeIcon icon={faRocket} style={{ marginRight: '6px' }} />
              Deployments & Deletions ({deploymentLogs.length})
            </Tab>
          </TabList>

          <TabPanels>
            {/* Tab 1: Course Modifications */}
            <TabPanel p={0}>
              {Object.keys(courseLogsByApp).length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={12} color={mutedTextColor}>
                  <FontAwesomeIcon icon={faInfoCircle} size="2x" style={{ marginBottom: '8px', opacity: 0.4 }} />
                  <Text fontSize="sm" fontWeight="500">No modification logs found matching your criteria.</Text>
                </Flex>
              ) : (
                <Accordion allowMultiple>
                  <AnimatePresence>
                    {Object.entries(courseLogsByApp).map(([appId, appLogs]) => (
                      <AccordionItem
                        key={appId}
                        border="1px solid"
                        borderColor={borderColor}
                        borderRadius="xl"
                        overflow="hidden"
                        mb={3}
                        bg={cardBg}
                        _hover={{ borderColor: '#0ea5e9' }}
                        transition="all 0.2s ease"
                      >
                        <h2>
                          <AccordionButton
                            _expanded={{ bg: 'rgba(14, 165, 233, 0.05)' }}
                            py={3.5}
                            px={4}
                          >
                            <Box flex="1" textAlign="left" fontWeight="700" color={titleColor} fontSize="sm" display="flex" alignItems="center" gap={2}>
                              <Text>{apps[appId]?.name || 'Unknown Course'}</Text>
                              <Tooltip label={`Copy Course ID: ${appId}`} placement="top" hasArrow borderRadius="md">
                                <IconButton
                                  aria-label="View Course ID"
                                  icon={<FontAwesomeIcon icon={faCopy} size="xs" />}
                                  size="xs"
                                  variant="ghost"
                                  color={mutedTextColor}
                                  _hover={{ color: '#0ea5e9', bg: 'rgba(14, 165, 233, 0.1)' }}
                                  onClick={e => {
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(appId)
                                    toast({
                                      title: 'Course ID Copied',
                                      description: appId,
                                      status: 'success',
                                      duration: 2000,
                                      isClosable: true
                                    })
                                  }}
                                />
                              </Tooltip>
                            </Box>
                            <Flex align="center" gap={3}>
                              <Badge
                                bg="rgba(14, 165, 233, 0.1)"
                                color="#0284c7"
                                borderRadius="md"
                                px={2.5}
                                py={0.5}
                                fontSize="10px"
                                fontWeight="700"
                              >
                                {appLogs.length} {appLogs.length === 1 ? 'LOG' : 'LOGS'}
                              </Badge>
                              <AccordionIcon color={mutedTextColor} />
                            </Flex>
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4} pt={2} px={4}>
                          <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor={borderColor}>
                            <Table variant="simple" size="sm">
                              <Thead bg={tableHeaderBg}>
                                <Tr>
                                  <Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">TIMESTAMP</Th>
                                  <Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">USER</Th>
                                  <Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">ACTION</Th>
                                  <Th py={2.5} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">DETAILS</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {appLogs.map(log => (
                                  <Tr key={log.id} _hover={{ bg: hoverBg }}>
                                    <Td py={2.5} px={3} whiteSpace="nowrap" color={mutedTextColor} fontSize="xs">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </Td>
                                    <Td py={2.5} px={3} fontWeight="600" color={titleColor} fontSize="xs">
                                      <HStack spacing={1.5}>
                                        <Text>{log.userEmail}</Text>
                                        {log.userId && (
                                          <Tooltip label={`Copy User ID: ${log.userId}`} placement="top" hasArrow borderRadius="md">
                                            <IconButton
                                              aria-label="View User ID"
                                              icon={<FontAwesomeIcon icon={faCopy} size="xs" />}
                                              size="xs"
                                              variant="ghost"
                                              color={mutedTextColor}
                                              _hover={{ color: '#0ea5e9' }}
                                              onClick={() => {
                                                navigator.clipboard.writeText(log.userId!)
                                                toast({
                                                  title: 'User ID Copied',
                                                  description: log.userId,
                                                  status: 'success',
                                                  duration: 2000,
                                                  isClosable: true
                                                })
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </HStack>
                                    </Td>
                                    <Td py={2.5} px={3}>
                                      <Badge
                                        colorScheme={getActionColorScheme(log.action)}
                                        variant="subtle"
                                        borderRadius="md"
                                        px={2}
                                        py={0.5}
                                        fontSize="9px"
                                        fontWeight="700"
                                      >
                                        {log.action}
                                      </Badge>
                                    </Td>
                                    <Td py={2.5} px={3}>
                                      <DetailsCell details={log.details} />
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </AnimatePresence>
                </Accordion>
              )}
            </TabPanel>

            {/* Tab 2: Deployments & Deletions */}
            <TabPanel p={0}>
              {deploymentLogs.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={12} color={mutedTextColor}>
                  <FontAwesomeIcon icon={faInfoCircle} size="2x" style={{ marginBottom: '8px', opacity: 0.4 }} />
                  <Text fontSize="sm" fontWeight="500">No deployment or deletion logs found matching your criteria.</Text>
                </Flex>
              ) : (
                <Box overflowX="auto" borderRadius="lg" border="1px solid" borderColor={borderColor}>
                  <Table variant="simple" size="sm">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th py={3} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">TIMESTAMP</Th>
                        <Th py={3} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">COURSE</Th>
                        <Th py={3} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">INSTRUCTOR</Th>
                        <Th py={3} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">ACTION</Th>
                        <Th py={3} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">PERFORMED BY</Th>
                        <Th py={3} px={3} color={mutedTextColor} fontSize="10px" fontWeight="700" letterSpacing="wider">DETAILS</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {deploymentLogs.map(log => (
                        <Tr key={log.id} _hover={{ bg: hoverBg }}>
                          <Td py={3} px={3} whiteSpace="nowrap" color={mutedTextColor} fontSize="xs">
                            {new Date(log.timestamp).toLocaleString()}
                          </Td>
                          <Td py={3} px={3} fontWeight="700" color={titleColor} fontSize="xs">
                            <HStack spacing={1.5}>
                              <Text>{apps[log.appId]?.name || 'Unknown Course'}</Text>
                              <Tooltip label={`Copy Course ID: ${log.appId}`} placement="top" hasArrow borderRadius="md">
                                <IconButton
                                  aria-label="View Course ID"
                                  icon={<FontAwesomeIcon icon={faCopy} size="xs" />}
                                  size="xs"
                                  variant="ghost"
                                  color={mutedTextColor}
                                  _hover={{ color: '#0ea5e9' }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(log.appId)
                                    toast({
                                      title: 'Course ID Copied',
                                      description: log.appId,
                                      status: 'success',
                                      duration: 2000,
                                      isClosable: true
                                    })
                                  }}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                          <Td py={3} px={3} color={mutedTextColor} fontSize="xs" fontWeight="500">
                            {apps[log.appId]?.creator || 'Unknown'}
                          </Td>
                          <Td py={3} px={3}>
                            <Badge
                              colorScheme={getActionColorScheme(log.action)}
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                              py={0.5}
                              fontSize="9px"
                              fontWeight="700"
                            >
                              {log.action}
                            </Badge>
                          </Td>
                          <Td py={3} px={3} fontWeight="600" color={titleColor} fontSize="xs">
                            <HStack spacing={1.5}>
                              <Text>{log.userEmail}</Text>
                              {log.userId && (
                                <Tooltip label={`Copy User ID: ${log.userId}`} placement="top" hasArrow borderRadius="md">
                                  <IconButton
                                    aria-label="View User ID"
                                    icon={<FontAwesomeIcon icon={faCopy} size="xs" />}
                                    size="xs"
                                    variant="ghost"
                                    color={mutedTextColor}
                                    _hover={{ color: '#0ea5e9' }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(log.userId!)
                                      toast({
                                        title: 'User ID Copied',
                                        description: log.userId,
                                        status: 'success',
                                        duration: 2000,
                                        isClosable: true
                                      })
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                          <Td py={3} px={3}>
                            <DetailsCell details={log.details} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Flex>
  )
}
